/**
 * Auth Modal Manager
 *
 * Manages the state and switching between login, signup, and password reset modals.
 * Provides a single component to control all auth modals.
 */

import React, { useState, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import LoginModal from './LoginModal';
import SignupModal from './SignupModal';
import PasswordResetModal from './PasswordResetModal';

// Create context for auth modal state
const AuthModalContext = createContext(null);

// Custom hook to use auth modal context
export const useAuthModals = () => {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error('useAuthModals must be used within AuthModalManager');
  }
  return context;
};

const AuthModalManager = ({ children }) => {
  const [activeModal, setActiveModal] = useState(null); // 'login' | 'signup' | 'password-reset' | null

  const openLogin = () => setActiveModal('login');
  const openSignup = () => setActiveModal('signup');
  const openPasswordReset = () => setActiveModal('password-reset');
  const closeModals = () => setActiveModal(null);

  const contextValue = {
    openLogin,
    openSignup,
    openPasswordReset,
    closeModals,
  };

  return (
    <AuthModalContext.Provider value={contextValue}>
      {children}

      {/* Modals */}
      <LoginModal
        isOpen={activeModal === 'login'}
        onClose={closeModals}
        onSwitchToSignup={() => setActiveModal('signup')}
        onSwitchToPasswordReset={() => setActiveModal('password-reset')}
      />

      <SignupModal
        isOpen={activeModal === 'signup'}
        onClose={closeModals}
        onSwitchToLogin={() => setActiveModal('login')}
      />

      <PasswordResetModal
        isOpen={activeModal === 'password-reset'}
        onClose={closeModals}
        onSwitchToLogin={() => setActiveModal('login')}
      />
    </AuthModalContext.Provider>
  );
};

AuthModalManager.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthModalManager;
